/**
 * DesignCanvas — 中间画布区域
 *
 * Renders bands as horizontal strips. Each band is a drop target.
 * Elements inside bands can be moved by dragging. Selected element
 * shows resize handles.
 *
 * The canvas mimics an A4 page (210mm wide) scaled to fit.
 */

import { useRef, useState, useCallback, type MouseEvent as ReactMouseEvent } from 'react';
import {
  type DesignTemplate,
  type DesignBand,
  type DesignElement,
  type PaletteItem,
  type BandType,
  BAND_TYPE_CONFIG,
  uid,
} from './types';
import { Plus, Trash2, GripVertical } from 'lucide-react';

// ── Page dimension constants (mm → px at 96dpi ≈ 3.78 px/mm) ─
const MM_PX = 3.78;
const PAGE_WIDTHS: Record<string, number> = { A4: 210, A3: 297, Letter: 216, Legal: 216 };
const CANVAS_SCALE = 0.85; // scale factor for comfortable viewport fit

interface DesignCanvasProps {
  template: DesignTemplate;
  selectedBandId: string | null;
  selectedElementId: string | null;
  onSelectElement: (bandId: string, elementId: string) => void;
  onDeselectAll: () => void;
  onAddElement: (bandId: string, element: DesignElement) => void;
  onMoveElement: (bandId: string, elementId: string, x: number, y: number) => void;
  onResizeElement: (bandId: string, elementId: string, w: number, h: number) => void;
  onDeleteElement: (bandId: string, elementId: string) => void;
  onResizeBand: (bandId: string, height: number) => void;
  onAddBand: (band: DesignBand) => void;
  onRemoveBand: (bandId: string) => void;
}

export default function DesignCanvas({
  template,
  selectedBandId,
  selectedElementId,
  onSelectElement,
  onDeselectAll,
  onAddElement,
  onMoveElement,
  onResizeElement,
  onDeleteElement,
  onResizeBand,
  onAddBand,
  onRemoveBand,
}: DesignCanvasProps) {
  const pageWidth = (PAGE_WIDTHS[template.pageSize] ?? 210) * MM_PX * CANVAS_SCALE;
  const contentWidth = pageWidth - (template.margins.left + template.margins.right) * MM_PX * CANVAS_SCALE;

  return (
    <div
      className="flex-1 overflow-auto bg-[#e8e8e8] dark:bg-zinc-900 p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) onDeselectAll();
      }}
    >
      {/* Page */}
      <div
        className="mx-auto bg-white dark:bg-zinc-800 shadow-lg rounded-sm relative"
        style={{
          width: pageWidth,
          minHeight: 400,
          paddingLeft: template.margins.left * MM_PX * CANVAS_SCALE,
          paddingRight: template.margins.right * MM_PX * CANVAS_SCALE,
          paddingTop: template.margins.top * MM_PX * CANVAS_SCALE,
          paddingBottom: template.margins.bottom * MM_PX * CANVAS_SCALE,
        }}
      >
        {/* Ruler left marker */}
        <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-primary/20" style={{ left: template.margins.left * MM_PX * CANVAS_SCALE }} />
        <div className="absolute right-0 top-0 bottom-0 w-[1px] bg-primary/20" style={{ right: template.margins.right * MM_PX * CANVAS_SCALE }} />

        {/* Bands */}
        {template.bands.map((band) => (
          <BandStrip
            key={band.id}
            band={band}
            contentWidth={contentWidth}
            isSelected={selectedBandId === band.id}
            selectedElementId={selectedBandId === band.id ? selectedElementId : null}
            onSelectElement={(elId) => onSelectElement(band.id, elId)}
            onDeselectAll={onDeselectAll}
            onAddElement={(el) => onAddElement(band.id, el)}
            onMoveElement={(elId, x, y) => onMoveElement(band.id, elId, x, y)}
            onResizeElement={(elId, w, h) => onResizeElement(band.id, elId, w, h)}
            onDeleteElement={(elId) => onDeleteElement(band.id, elId)}
            onResizeBand={(h) => onResizeBand(band.id, h)}
            onRemoveBand={() => onRemoveBand(band.id)}
          />
        ))}

        {/* Add band button */}
        <AddBandButton onAddBand={onAddBand} />
      </div>
    </div>
  );
}

// ── Band Strip ───────────────────────────────────────────────

interface BandStripProps {
  band: DesignBand;
  contentWidth: number;
  isSelected: boolean;
  selectedElementId: string | null;
  onSelectElement: (id: string) => void;
  onDeselectAll: () => void;
  onAddElement: (el: DesignElement) => void;
  onMoveElement: (id: string, x: number, y: number) => void;
  onResizeElement: (id: string, w: number, h: number) => void;
  onDeleteElement: (id: string) => void;
  onResizeBand: (height: number) => void;
  onRemoveBand: () => void;
}

function BandStrip({
  band,
  contentWidth,
  isSelected,
  selectedElementId,
  onSelectElement,
  onDeselectAll,
  onAddElement,
  onMoveElement,
  onResizeElement,
  onDeleteElement,
  onResizeBand,
  onRemoveBand,
}: BandStripProps) {
  const config = BAND_TYPE_CONFIG[band.type];
  const bandRef = useRef<HTMLDivElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  // ── Drop handling ──────────────────────────────
  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (e.dataTransfer.types.includes('application/x-designer-element')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback(() => setIsDragOver(false), []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const data = e.dataTransfer.getData('application/x-designer-element');
      if (!data) return;
      const item: PaletteItem = JSON.parse(data);
      const rect = bandRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = Math.max(0, e.clientX - rect.left);
      const y = Math.max(0, e.clientY - rect.top);
      const newEl: DesignElement = {
        id: uid(),
        type: item.type,
        x,
        y,
        width: item.defaultWidth,
        height: item.defaultHeight,
        props: getDefaultProps(item.type),
      };
      onAddElement(newEl);
    },
    [onAddElement],
  );

  // ── Band resize (drag bottom edge) ────────────
  const handleResizeStart = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      const startY = e.clientY;
      const startH = band.height;

      const onMouseMove = (ev: globalThis.MouseEvent) => {
        const delta = ev.clientY - startY;
        onResizeBand(Math.max(20, startH + delta));
      };
      const onMouseUp = () => {
        setIsResizing(false);
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    },
    [band.height, onResizeBand],
  );

  return (
    <div className="relative mb-0.5 group/band">
      {/* Band label */}
      <div
        className="flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-medium select-none"
        style={{ color: config.color, borderLeft: `3px solid ${config.color}` }}
      >
        <GripVertical className="h-3 w-3 opacity-40" />
        <span>{config.label}</span>
        <span className="text-muted-foreground/50 font-normal">— {config.hint}</span>
        <span className="ml-auto text-muted-foreground/40">{band.height}px</span>
        <button
          onClick={onRemoveBand}
          className="opacity-0 group-hover/band:opacity-60 hover:!opacity-100 transition-opacity p-0.5"
          title="Remove band"
        >
          <Trash2 className="h-3 w-3 text-red-400" />
        </button>
      </div>

      {/* Band content area — drop target */}
      <div
        ref={bandRef}
        className={`relative border border-dashed transition-colors ${
          isDragOver
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/15 hover:border-muted-foreground/30'
        }`}
        style={{
          width: contentWidth,
          height: band.height,
          borderLeftColor: config.color,
          borderLeftWidth: 2,
          borderLeftStyle: 'solid',
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) onDeselectAll();
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Elements */}
        {band.elements.map((el) => (
          <CanvasElement
            key={el.id}
            element={el}
            isSelected={selectedElementId === el.id}
            bandRef={bandRef}
            onSelect={() => onSelectElement(el.id)}
            onMove={(x, y) => onMoveElement(el.id, x, y)}
            onResize={(w, h) => onResizeElement(el.id, w, h)}
            onDelete={() => onDeleteElement(el.id)}
          />
        ))}

        {/* Empty state */}
        {band.elements.length === 0 && !isDragOver && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-[10px] text-muted-foreground/30">Drop elements here</span>
          </div>
        )}
      </div>

      {/* Resize handle */}
      <div
        className={`absolute bottom-0 left-0 right-0 h-1.5 cursor-ns-resize z-10
                    hover:bg-primary/20 ${isResizing ? 'bg-primary/30' : ''}`}
        onMouseDown={handleResizeStart}
      />
    </div>
  );
}

// ── Canvas Element ───────────────────────────────────────────

interface CanvasElementProps {
  element: DesignElement;
  isSelected: boolean;
  bandRef: React.RefObject<HTMLDivElement | null>;
  onSelect: () => void;
  onMove: (x: number, y: number) => void;
  onResize: (w: number, h: number) => void;
  onDelete: () => void;
}

function CanvasElement({
  element,
  isSelected,
  bandRef,
  onSelect,
  onMove,
  onResize,
  onDelete,
}: CanvasElementProps) {
  const elRef = useRef<HTMLDivElement>(null);

  // ── Element drag (move within band) ────────────
  const handleMouseDown = useCallback(
    (e: ReactMouseEvent) => {
      if ((e.target as HTMLElement).dataset.handle === 'resize') return;
      e.stopPropagation();
      onSelect();

      const startX = e.clientX;
      const startY = e.clientY;
      const ox = element.x;
      const oy = element.y;

      const onMouseMove = (ev: globalThis.MouseEvent) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        onMove(Math.max(0, ox + dx), Math.max(0, oy + dy));
      };
      const onMouseUp = () => {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    },
    [element.x, element.y, onMove, onSelect],
  );

  // ── Resize handle ─────────────────────────────
  const handleResizeStart = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const startX = e.clientX;
      const startY = e.clientY;
      const sw = element.width;
      const sh = element.height;

      const onMouseMove = (ev: globalThis.MouseEvent) => {
        const dw = ev.clientX - startX;
        const dh = ev.clientY - startY;
        onResize(Math.max(16, sw + dw), Math.max(8, sh + dh));
      };
      const onMouseUp = () => {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    },
    [element.width, element.height, onResize],
  );

  // ── Keyboard delete ───────────────────────────
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        onDelete();
      }
    },
    [onDelete],
  );

  return (
    <div
      ref={elRef}
      className={`absolute group/el cursor-move select-none ${
        isSelected
          ? 'ring-2 ring-primary ring-offset-1 z-20'
          : 'hover:ring-1 hover:ring-primary/40 z-10'
      }`}
      style={{
        left: element.x,
        top: element.y,
        width: element.width,
        height: element.height,
      }}
      tabIndex={0}
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeyDown}
    >
      {/* Element visual representation */}
      <ElementRenderer element={element} />

      {/* Selection handles */}
      {isSelected && (
        <>
          {/* Corner dots */}
          <div className="absolute -top-1 -left-1 w-2 h-2 bg-primary rounded-full" />
          <div className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full" />
          <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-primary rounded-full" />
          {/* Resize handle (bottom-right) */}
          <div
            data-handle="resize"
            className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-primary rounded-sm cursor-nwse-resize hover:bg-primary/80"
            onMouseDown={handleResizeStart}
          />
          {/* Delete button */}
          <button
            className="absolute -top-3 -right-3 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center
                       text-[8px] font-bold hover:bg-red-600 shadow-sm"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            title="Delete"
          >
            ×
          </button>
        </>
      )}
    </div>
  );
}

// ── Element Renderer ─────────────────────────────────────────

function ElementRenderer({ element }: { element: DesignElement }) {
  const base = 'w-full h-full flex items-center overflow-hidden text-[10px]';

  switch (element.type) {
    case 'Label':
      return (
        <div className={`${base} px-1 bg-yellow-50/60 border border-yellow-200/60 dark:bg-yellow-900/20 dark:border-yellow-800/40`}>
          <span className="truncate font-medium" style={{
            fontSize: element.props.fontSize ? `${element.props.fontSize}px` : undefined,
            fontWeight: element.props.bold ? 700 : undefined,
            fontStyle: element.props.italic ? 'italic' : undefined,
            textAlign: (element.props.textAlign as React.CSSProperties['textAlign']) ?? 'left',
          }}>
            {(element.props.text as string) || 'Text Label'}
          </span>
        </div>
      );

    case 'Field':
      return (
        <div className={`${base} px-1 bg-primary/5 border border-primary/20 dark:bg-primary/10 dark:border-primary/20`}>
          <span className="truncate text-primary font-mono">
            {'{'}{(element.props.dataBinding as string) || 'field_name'}{'}'}
          </span>
        </div>
      );

    case 'Image':
      return (
        <div className={`${base} bg-purple-50/40 border border-purple-200/60 dark:bg-purple-900/20 justify-center`}>
          <span className="text-lg">🖼️</span>
          <span className="text-[8px] text-muted-foreground ml-1 truncate">
            {(element.props.src as string) || 'image'}
          </span>
        </div>
      );

    case 'Table':
      return (
        <div className="w-full h-full bg-green-50/40 border border-green-200/60 dark:bg-green-900/20 p-0.5">
          <div className="w-full h-3 bg-green-200/40 dark:bg-green-800/40 rounded-[1px] mb-0.5" />
          <div className="w-full h-2 bg-green-100/40 dark:bg-green-900/30 rounded-[1px] mb-0.5" />
          <div className="w-full h-2 bg-green-50/40 dark:bg-green-950/20 rounded-[1px]" />
          <div className="text-[8px] text-green-600/60 mt-0.5 truncate">
            {(element.props.dataSource as string) || 'table data'}
          </div>
        </div>
      );

    case 'Line':
      return (
        <div className="w-full h-full flex items-center">
          <div
            className="w-full"
            style={{
              borderTop: `${(element.props.thickness as number) || 1}px solid ${(element.props.color as string) || '#333'}`,
            }}
          />
        </div>
      );

    case 'Rectangle':
      return (
        <div
          className="w-full h-full"
          style={{
            backgroundColor: (element.props.fillColor as string) || 'transparent',
            border: `${(element.props.borderWidth as number) || 1}px solid ${(element.props.borderColor as string) || '#999'}`,
            borderRadius: (element.props.borderRadius as number) ?? 0,
          }}
        />
      );

    case 'Barcode':
      return (
        <div className={`${base} bg-gray-50/60 border border-gray-200/60 dark:bg-gray-900/20 justify-center flex-col gap-0.5`}>
          <div className="flex gap-[1px]">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="w-[2px] bg-gray-800 dark:bg-gray-200" style={{ height: 14 + (i % 3) * 3 }} />
            ))}
          </div>
          <span className="text-[7px] text-muted-foreground">
            {(element.props.barcodeType as string) || 'Code128'}
          </span>
        </div>
      );

    case 'Checkbox':
      return (
        <div className={`${base} gap-1 px-1`}>
          <div className="w-3 h-3 border border-gray-400 rounded-sm flex items-center justify-center text-[8px]">✓</div>
          <span className="truncate text-muted-foreground">
            {(element.props.label as string) || 'Checkbox'}
          </span>
        </div>
      );

    case 'SummaryField':
      return (
        <div className={`${base} px-1 bg-orange-50/60 border border-orange-200/60 dark:bg-orange-900/20`}>
          <span className="truncate text-orange-600 dark:text-orange-400 font-mono">
            ∑ {(element.props.func as string) || 'sum'}({(element.props.dataBinding as string) || 'field'})
          </span>
        </div>
      );

    case 'PageNumber':
      return (
        <div className={`${base} px-1 bg-cyan-50/60 border border-cyan-200/60 dark:bg-cyan-900/20 justify-center`}>
          <span className="text-cyan-600 dark:text-cyan-400">Page # / #</span>
        </div>
      );

    default:
      return (
        <div className={`${base} bg-gray-100 border border-gray-200 justify-center`}>
          <span className="text-muted-foreground">{element.type}</span>
        </div>
      );
  }
}

// ── Add Band Button ──────────────────────────────────────────

const BAND_OPTIONS: BandType[] = [
  'ReportHeader', 'PageHeader', 'GroupHeader', 'Detail',
  'SubDetail', 'GroupFooter', 'PageFooter', 'ReportFooter',
];

function AddBandButton({ onAddBand }: { onAddBand: (band: DesignBand) => void }) {
  const [open, setOpen] = useState(false);

  const handleAdd = (type: BandType) => {
    const config = BAND_TYPE_CONFIG[type];
    onAddBand({
      id: uid(),
      type,
      height: type === 'Detail' ? 200 : 60,
      elements: [],
    });
    setOpen(false);
  };

  return (
    <div className="mt-3 flex justify-center">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-1 px-3 py-1.5 text-xs text-muted-foreground/60
                     border border-dashed border-muted-foreground/20 rounded hover:border-primary
                     hover:text-primary transition-colors"
        >
          <Plus className="h-3 w-3" />
          Add Band
        </button>
      ) : (
        <div className="flex flex-wrap gap-1 items-center">
          {BAND_OPTIONS.map((type) => {
            const cfg = BAND_TYPE_CONFIG[type];
            return (
              <button
                key={type}
                onClick={() => handleAdd(type)}
                className="px-2 py-1 text-[10px] rounded border hover:bg-accent transition-colors"
                style={{ borderColor: cfg.color, color: cfg.color }}
              >
                {cfg.label}
              </button>
            );
          })}
          <button
            onClick={() => setOpen(false)}
            className="px-2 py-1 text-[10px] rounded text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// ── Default props per element type ───────────────────────────

function getDefaultProps(type: string): Record<string, unknown> {
  switch (type) {
    case 'Label': return { text: 'Label', fontSize: 10, bold: false, italic: false, textAlign: 'left' };
    case 'Field': return { dataBinding: '', format: 'text' };
    case 'Image': return { src: '', objectFit: 'contain' };
    case 'Table': return { dataSource: '', columns: [] };
    case 'Line': return { direction: 'horizontal', thickness: 1, color: '#333333' };
    case 'Rectangle': return { fillColor: 'transparent', borderColor: '#999999', borderWidth: 1, borderRadius: 0 };
    case 'Barcode': return { barcodeType: 'Code128', dataBinding: '', showText: true };
    case 'Checkbox': return { dataBinding: '', label: 'Checkbox' };
    case 'SummaryField': return { func: 'sum', dataBinding: '', running: 'report' };
    case 'PageNumber': return {};
    default: return {};
  }
}
